/**
 * Community Votes Page
 *
 * Clean, elegant display of recent community battles and winners.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Footer } from '@/components/Footer';
import {
  ChevronRightIcon,
  LeaderboardIcon,
  StarIcon,
} from '@/components/icons/common';
import { IconCircle } from '@/components/panel/IconCircle';
import { ProtectedLink } from '@/components/ProtectedLink';
import { HeroPattern } from '@/components/shared/HeroPattern';
import { ProviderLogo } from '@/components/shared/ProviderLogo';
import { EmptyState, LoadingState } from '@/components/ui';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

// ============================================================================
// Types
// ============================================================================

interface RecentVote {
  runId: string;
  promptText: string;
  category: string | null;
  latestVote: string;
  totalVotes: number;
  uniqueVoters: number;
  winner: {
    name: string;
    modelKey: string;
    votes: number;
  } | null;
  runnerUp: {
    name: string;
    modelKey: string;
    votes: number;
  } | null;
}

// ============================================================================
// Icons
// ============================================================================

const ErrorIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8B2332"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ============================================================================
// Utility Functions
// ============================================================================

function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

// ============================================================================
// Sub-Components
// ============================================================================

interface StatsPillProps {
  totalVotes: number;
  tournamentCount: number;
}

const StatsPill = ({ totalVotes, tournamentCount }: StatsPillProps) => (
  <div
    className="inline-flex items-center gap-4 mt-8 px-5 py-2.5 rounded-full"
    style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}
  >
    <div className="flex items-center gap-2">
      <span className="font-serif text-xl font-medium text-white tabular-nums">
        {totalVotes.toLocaleString()}
      </span>
      <span
        className="text-xs uppercase tracking-wide"
        style={{ color: 'rgba(255,255,255,0.6)' }}
      >
        votes
      </span>
    </div>
    <div className="w-px h-4" style={{ background: 'rgba(255,255,255,0.2)' }} />
    <div className="flex items-center gap-2">
      <span className="font-serif text-xl font-medium text-white tabular-nums">
        {tournamentCount}
      </span>
      <span
        className="text-xs uppercase tracking-wide"
        style={{ color: 'rgba(255,255,255,0.6)' }}
      >
        tournaments
      </span>
    </div>
  </div>
);

const ErrorState = ({ message }: { message: string }) => (
  <div className="py-20 text-center">
    <IconCircle
      icon={<ErrorIcon />}
      backgroundColor="rgba(139,35,50,0.1)"
      size={56}
    />
    <p className="text-sm font-medium" style={{ color: '#8B2332' }}>
      {message}
    </p>
  </div>
);

const CommunityVotesEmptyState = () => (
  <EmptyState
    title="No votes yet"
    description="Be the first to vote! Head to the arena to start a tournament."
    action={
      <Link
        to="/arena"
        className="inline-flex px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
        style={{ background: '#8B2332' }}
      >
        Start Tournament
      </Link>
    }
  />
);

interface WinnerBannerProps {
  winner: RecentVote['winner'];
}

const WinnerBanner = ({ winner }: WinnerBannerProps) => {
  if (!winner) return null;

  return (
    <div
      className="px-5 py-4 flex items-center gap-4"
      style={{
        background: 'linear-gradient(135deg, #F8F6F3 0%, #FFFFFF 100%)',
        borderBottom: '1px solid #E8E3DD',
      }}
    >
      <ProviderLogo modelKey={winner.modelKey} size={44} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate" style={{ color: '#1A1A1A' }}>
            {winner.name}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <StarIcon size={14} />
          <span className="text-xs font-medium" style={{ color: '#B8860B' }}>
            Winner
          </span>
        </div>
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronRightIcon color="#8B2332" />
      </div>
    </div>
  );
};

interface VoteMetaProps {
  latestVote: string;
  totalVotes: number;
  category: string | null;
}

const VoteMeta = ({ latestVote, totalVotes, category }: VoteMetaProps) => (
  <div className="flex items-center gap-3 flex-wrap">
    <span className="text-xs tabular-nums" style={{ color: '#8B8580' }}>
      {timeAgo(latestVote)}
    </span>
    <span style={{ color: '#DDD8D2' }}>·</span>
    <span className="text-xs tabular-nums" style={{ color: '#8B8580' }}>
      {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
    </span>
    {category && (
      <>
        <span style={{ color: '#DDD8D2' }}>·</span>
        <span className="text-xs" style={{ color: '#8B8580' }}>
          {category}
        </span>
      </>
    )}
  </div>
);

const VoteCard = ({ vote }: { vote: RecentVote }) => {
  return (
    <ProtectedLink to={`/arena/${vote.runId}/results`} className="block group">
      <div
        className="rounded-xl overflow-hidden transition-all duration-200 group-hover:shadow-lg group-hover:-translate-y-1"
        style={{
          background: '#FFFFFF',
          border: '1px solid #E8E3DD',
        }}
      >
        <WinnerBanner winner={vote.winner} />

        <div className="px-5 py-4">
          <p
            className="text-sm leading-relaxed mb-4"
            style={{ color: '#4A4541' }}
          >
            {truncateText(vote.promptText, 180)}
          </p>
          <VoteMeta
            latestVote={vote.latestVote}
            totalVotes={vote.totalVotes}
            category={vote.category}
          />
        </div>
      </div>
    </ProtectedLink>
  );
};

const HeroSection = ({
  totalVotes,
  tournamentCount,
}: {
  totalVotes: number;
  tournamentCount: number;
}) => (
  <HeroPattern>
    <div className="max-w-5xl mx-auto px-8 pt-24 pb-16">
      <div className="text-center fade-up">
        <h1 className="font-serif text-4xl md:text-5xl font-medium mb-4 text-white">
          Community Votes
        </h1>
        <p
          className="text-base md:text-lg max-w-xl mx-auto leading-relaxed"
          style={{ color: 'rgba(255,255,255,0.7)' }}
        >
          Recent battles from the community. Click any card to see the full
          tournament results.
        </p>
        {totalVotes > 0 && (
          <StatsPill
            totalVotes={totalVotes}
            tournamentCount={tournamentCount}
          />
        )}
      </div>
    </div>
  </HeroPattern>
);

const VotesGrid = ({ votes }: { votes: RecentVote[] }) => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
    {votes.map((vote, idx) => (
      <div
        key={vote.runId}
        className="fade-up"
        style={{ animationDelay: `${Math.min(idx * 40, 400)}ms` }}
      >
        <VoteCard vote={vote} />
      </div>
    ))}
  </div>
);

const LeaderboardLink = () => (
  <section className="pb-16 px-8">
    <div className="max-w-6xl mx-auto flex justify-center">
      <ProtectedLink
        to="/leaderboard"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all hover:opacity-90"
        style={{
          background: '#1A1A1A',
          color: '#FFFFFF',
        }}
      >
        <LeaderboardIcon />
        View Full Leaderboard
      </ProtectedLink>
    </div>
  </section>
);

// ============================================================================
// Main Component
// ============================================================================

export default function CommunityVotesPage() {
  const [votes, setVotes] = useState<RecentVote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadVotes() {
      try {
        setLoading(true);
        const response = await apiRequest(
          '/api/leaderboard/recent-votes?limit=24'
        );
        setVotes(response.votes || []);
      } catch (err: any) {
        logError('Failed to load community votes', err);
        setError(err.message || 'Failed to load votes');
      } finally {
        setLoading(false);
      }
    }
    loadVotes();
  }, []);

  const totalVotes = votes.reduce((sum, v) => sum + v.totalVotes, 0);

  return (
    <div className="min-h-screen" style={{ background: '#F5F0EB' }}>
      <style>{`
        .fade-up { animation: fade-up 0.4s ease-out forwards; opacity: 0; }
      `}</style>

      <HeroSection totalVotes={totalVotes} tournamentCount={votes.length} />

      <section className="py-12 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          {error ? (
            <ErrorState message={error} />
          ) : loading ? (
            <div className="py-12">
              <LoadingState />
            </div>
          ) : votes.length === 0 ? (
            <CommunityVotesEmptyState />
          ) : (
            <VotesGrid votes={votes} />
          )}
        </div>
      </section>

      <LeaderboardLink />

      <Footer />
    </div>
  );
}
